/* ------------------------------------------------------------------ */
/*  Clinical conversation logic (PRD.274) — pure + testable.           */
/*                                                                      */
/*  Active-conversation selection, sort order, multi-device            */
/*  reconciliation, legacy grouping/backfill, segment stats, and       */
/*  date/time presentation. No React/PowerSync — mirrors the           */
/*  allergy-review-logic style so it stays unit-testable.              */
/* ------------------------------------------------------------------ */

export type ConversationStatus = 'active' | 'completed' | 'archived';

export interface ConversationLike {
  id: string;
  status: string | null;
  started_at: string | null;
  deleted_at?: string | null;
}

export interface RecordingLike {
  id: string;
  conversation_id: string | null;
  recorded_at: string | null;
  transcript: string | null;
  duration_ms: number | null;
}

/** Deterministic legacy conversation id for a visit (matches the backfill SQL). */
export const LEGACY_CONVERSATION_PREFIX = 'legacy_cc_';
export function legacyConversationId(visitId: string): string {
  return `${LEGACY_CONVERSATION_PREFIX}${visitId}`;
}

/** The conversation a recording belongs to — its own id, or the visit's legacy id. */
export function conversationIdForRecording(
  rec: Pick<RecordingLike, 'conversation_id'>,
  visitId: string,
): string {
  return rec.conversation_id ?? legacyConversationId(visitId);
}

/** Sort conversations newest-first (by started_at desc; id as a stable tiebreak). */
export function sortConversationsNewestFirst<T extends ConversationLike>(conversations: readonly T[]): T[] {
  return [...conversations].sort((a, b) => {
    const cmp = (b.started_at ?? '').localeCompare(a.started_at ?? '');
    return cmp !== 0 ? cmp : b.id.localeCompare(a.id);
  });
}

function isLive(c: ConversationLike): boolean {
  return !c.deleted_at;
}

/**
 * The id of the active conversation for a visit/section, or null. If multiple
 * rows are `active` (a multi-device race), the most-recently-started wins —
 * matching `reconcileActiveConversations`.
 */
export function selectActiveConversationId(conversations: readonly ConversationLike[]): string | null {
  const active = sortConversationsNewestFirst(conversations.filter((c) => isLive(c) && c.status === 'active'));
  return active[0]?.id ?? null;
}

/** The conversation expanded by default: the active one, else the newest live one. */
export function defaultExpandedConversationId(conversations: readonly ConversationLike[]): string | null {
  const activeId = selectActiveConversationId(conversations);
  if (activeId) return activeId;
  const live = sortConversationsNewestFirst(conversations.filter(isLive));
  return live[0]?.id ?? null;
}

/**
 * Deterministic multi-device reconciliation: when more than one live
 * conversation is `active`, keep the most-recently-started active and mark the
 * rest for completion. Returns the winner + the ids to complete.
 */
export function reconcileActiveConversations(conversations: readonly ConversationLike[]): {
  activeId: string | null;
  toComplete: string[];
} {
  const active = sortConversationsNewestFirst(conversations.filter((c) => isLive(c) && c.status === 'active'));
  if (active.length <= 1) return { activeId: active[0]?.id ?? null, toComplete: [] };
  const [winner, ...losers] = active;
  return { activeId: winner.id, toComplete: losers.map((c) => c.id) };
}

/** Segment count + total recorded duration for a conversation's recordings. */
export function conversationSegmentStats(recordings: readonly RecordingLike[]): {
  segmentCount: number;
  totalDurationMs: number;
} {
  return {
    segmentCount: recordings.length,
    totalDurationMs: recordings.reduce((sum, r) => sum + (r.duration_ms ?? 0), 0),
  };
}

/* ── Legacy backfill grouping (mirrors PRD.274_conversations_backfill.sql) ── */

export interface LegacyConversationGroup {
  conversationId: string;
  visitId: string;
  startedAt: string | null;
  recordingIds: string[];
}

/**
 * Group un-linked recordings (conversation_id null) into one legacy conversation
 * per visit, using the earliest recorded_at as started_at. Pure mirror of the
 * backfill SQL so the grouping is unit-testable.
 */
export function backfillLegacyGroups(
  recordings: readonly (RecordingLike & { visit_id: string | null })[],
): LegacyConversationGroup[] {
  const byVisit = new Map<string, LegacyConversationGroup>();
  for (const r of recordings) {
    if (r.conversation_id) continue; // already linked
    const visitId = r.visit_id;
    if (!visitId) continue;
    const existing = byVisit.get(visitId);
    if (!existing) {
      byVisit.set(visitId, {
        conversationId: legacyConversationId(visitId),
        visitId,
        startedAt: r.recorded_at,
        recordingIds: [r.id],
      });
    } else {
      existing.recordingIds.push(r.id);
      if (r.recorded_at && (existing.startedAt === null || r.recorded_at < existing.startedAt)) {
        existing.startedAt = r.recorded_at;
      }
    }
  }
  return [...byVisit.values()];
}

/* ── Date/time presentation ── */

const HYPHEN = '·';

/**
 * "Jul 21, 2026 · 5:42 PM" in the given time zone (default: browser local).
 * Deterministic for a fixed `timeZone` (tests pass 'UTC'). Date/time is
 * presentation metadata only — never injected into generated summary text.
 */
export function formatConversationDateTime(
  iso: string | null | undefined,
  opts?: { timeZone?: string },
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: opts?.timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  const month = get('month');
  const day = get('day');
  const year = get('year');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod');
  return `${month} ${day}, ${year} ${HYPHEN} ${hour}:${minute} ${dayPeriod}`;
}
